import { GridApi, ColumnEvent, ColumnEverythingChangedEvent, ColumnState } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { localStorageService } from '../../utils/storageUtils';
import { eventSources } from '../../shared/gridEdit/constants';

type UseRestoreColumnsStateOptions = {
  defaultColumnGroups?: string[],
  preprocessBeforeLoad?: (columnsState: ColumnState[]) => ColumnState[],
  preprocessBeforeSave?: (columnState: ColumnState[]) => ColumnState[],
}

const identity = <T>(arg: T): T => arg;

const defOptions: UseRestoreColumnsStateOptions = {
  preprocessBeforeLoad: identity,
  preprocessBeforeSave: identity,
};

const ignoredColumnEventSources = [
  eventSources.API,
  eventSources.SIZE_COLUMNS_TO_FIT,
];

export const useRestoreColumnsState = (
  storageKey: string,
  gridApi: GridApi | undefined | null,
  {
    defaultColumnGroups,
    preprocessBeforeLoad,
    preprocessBeforeSave,
  } : UseRestoreColumnsStateOptions = defOptions
) => {
  const initialColumnsState = useMemo(() => [
    ...defaultColumnGroups?.map(colId => ({
      colId,
      rowGroup: true,
    })) ?? [],
  ]
  , [defaultColumnGroups]);

  const columnStateRestored = useRef(false);

  const saveColumnsState = useCallback((state: ColumnState[]) => {
    localStorageService.setItem(storageKey, state);
  }, [storageKey]);

  const loadColumnsState = useCallback(() =>
    localStorageService.getItem<ColumnState[]>(storageKey, initialColumnsState),
  [storageKey, initialColumnsState]
  ) as any;

  const onColumnStateChange = useCallback((event: ColumnEvent) => {
    if (ignoredColumnEventSources.includes(event.source)) {
      return;
    }
    const columnsState = event.api.getColumnState();
    saveColumnsState(preprocessBeforeSave ? preprocessBeforeSave(columnsState) : columnsState);
  }, [saveColumnsState, preprocessBeforeSave]);

  const onColumnEverythingChanged = ({ api, source }: ColumnEverythingChangedEvent) => {
    if (columnStateRestored.current) {
      columnStateRestored.current = false;
    } else if (api && (source !== eventSources.TOOL_PANEL_UI && source !== eventSources.API)) {
      api.applyColumnState({
        state: preprocessBeforeLoad ? preprocessBeforeLoad(loadColumnsState()) : loadColumnsState(),
      });
      columnStateRestored.current = true;
    }
  };

  const resetColumnState = useCallback(() => {
    localStorageService.setItem(storageKey, []);
    gridApi?.resetColumnState();
  }, [storageKey, gridApi]);

  useEffect(() => {
    gridApi?.resetColumnState();
  }, [storageKey, gridApi]);

  return {
    onColumnEverythingChanged,
    onColumnVisible: onColumnStateChange,
    onColumnPinned: onColumnStateChange,
    onColumnResized: onColumnStateChange,
    onColumnMoved: onColumnStateChange,
    onColumnRowGroupChanged: onColumnStateChange,
    resetColumnState,
  };
};
